import { getCardBorderStyle, getSurfaceBorderColor, getSurfaceBorderStyle } from "@/app/application/theme";
import { sharedSecondaryButtonSx } from "@/styles/sharedNavStyles";
import { alpha } from "@mui/material";
import type { Theme } from "@mui/material/styles";

export const searchPalettePaperSx = (theme: Theme, isMobile = false) => ({
	borderRadius: isMobile ? 0 : 3,
	overflow: "hidden",
	border: isMobile ? "none" : getCardBorderStyle(theme),
	backgroundImage: "none",
	boxShadow: isMobile
		? "none"
		: `0 28px 90px ${alpha(theme.palette.common.black, 0.22)}`,
	maxHeight: { xs: "82vh", sm: "72vh" },
});

export const searchPaletteInputSx = (theme: Theme) => ({
	"& .MuiInputBase-root": {
		borderRadius: 2.5,
		bgcolor: theme.palette.background.paper,
		border: getSurfaceBorderStyle(theme, "strong"),
		boxShadow: `0 1px 10px ${alpha(theme.palette.common.black, 0.08)}`,
		"&.Mui-focused": {
			boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.14)}`,
		},
	},
});

export const searchPaletteRowSx = (theme: Theme, selected: boolean) => ({
	borderRadius: 2,
	border: getSurfaceBorderStyle(theme, selected ? "strong" : "subtle"),
	px: 1.25,
	py: 0.875,
	alignItems: "stretch",
	gap: 1,
	cursor: "pointer",
	userSelect: "none",
	bgcolor: selected
		? alpha(theme.palette.primary.main, 0.09)
		: "background.paper",
	boxShadow: selected
		? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.14)}, 0 12px 24px ${alpha(
				theme.palette.common.black,
				0.08,
			)}`
		: "none",
	transition:
		"box-shadow 120ms ease, border-color 120ms ease, transform 60ms ease",
	"&:hover": {
		borderColor: alpha(theme.palette.primary.main, 0.32),
	},
	"&.Mui-focusVisible": {
		outline: "none",
		boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.18)}, 0 12px 24px ${alpha(
			theme.palette.common.black,
			0.08,
		)}`,
	},
	"&:active": { transform: "translateY(0.5px)" },
	"&:hover .hover-actions": { opacity: 1 },
});

export const searchPaletteNeutralChipSx = (theme: Theme, selected: boolean) => ({
	borderRadius: 2,
	fontWeight: selected ? 900 : 800,
	height: 28,
	transition: "background-color 120ms ease, border-color 120ms ease, color 120ms ease, box-shadow 120ms ease",
	...(selected
		? {
				bgcolor: alpha(theme.palette.primary.main, 0.14),
				borderColor: alpha(theme.palette.primary.main, 0.36),
				color: theme.palette.primary.main,
				boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`,
			}
		: {
				bgcolor: alpha(theme.palette.text.primary, 0.015),
				borderColor: getSurfaceBorderColor(theme, "strong"),
				color: theme.palette.text.secondary,
			}),
});

export const searchPaletteAccentChipSx = (
	theme: Theme,
	accentColor: string,
	selected: boolean,
) => ({
	borderRadius: 2,
	fontWeight: selected ? 900 : 800,
	height: 28,
	transition: "background-color 120ms ease, border-color 120ms ease, color 120ms ease, box-shadow 120ms ease",
	...(selected
		? {
				bgcolor: alpha(accentColor, 0.14),
				borderColor: alpha(accentColor, 0.34),
				color: accentColor,
				boxShadow: `0 0 0 1px ${alpha(accentColor, 0.1)}`,
			}
		: {
				bgcolor: alpha(accentColor, 0.05),
				borderColor: alpha(accentColor, 0.28),
				color: accentColor,
			}),
});

export const searchPaletteCountChipSx = (accentColor: string) => ({
	height: 22,
	borderRadius: 2,
	fontWeight: 900,
	bgcolor: alpha(accentColor, 0.08),
	borderColor: alpha(accentColor, 0.24),
	color: accentColor,
});

export const searchPalettePreviewPanelSx = (theme: Theme) => ({
	borderLeft: getSurfaceBorderStyle(theme, "strong"),
	p: 2,
	overflow: "auto",
	minHeight: 0,
	display: "flex",
	flexDirection: "column" as const,
	gap: 1.25,
	backgroundColor:
		theme.palette.mode === "dark"
			? alpha(theme.palette.common.black, 0.04)
			: alpha(theme.palette.common.black, 0.015),
});

export const searchPalettePreviewCardSx = (theme: Theme) => ({
	border: getSurfaceBorderStyle(theme, "subtle"),
	borderRadius: 2.5,
	bgcolor:
		theme.palette.mode === "dark"
			? alpha(theme.palette.common.white, 0.025)
			: alpha(theme.palette.common.white, 0.9),
	boxShadow: `0 4px 16px ${alpha(
		theme.palette.common.black,
		theme.palette.mode === "dark" ? 0.14 : 0.04,
	)}`,
});

export const searchPaletteMetaTextSx = {
	display: "-webkit-box",
	WebkitLineClamp: 1,
	WebkitBoxOrient: "vertical" as const,
	overflow: "hidden",
	lineHeight: 1.45,
};

export const searchPaletteSummaryTextSx = {
	display: "-webkit-box",
	WebkitLineClamp: 2,
	WebkitBoxOrient: "vertical" as const,
	overflow: "hidden",
	lineHeight: 1.35,
};

export const searchPaletteQuickLookToggleSx = (
	theme: Theme,
	_active: boolean,
) => ({
	...(void _active, {}),
	...(sharedSecondaryButtonSx as object),
	borderColor: theme.palette.primary.main,
	bgcolor: theme.palette.primary.main,
	color: `${theme.palette.common.white} !important`,
	boxShadow: "none",
	"& .MuiButton-startIcon": {
		color: `${theme.palette.common.white} !important`,
	},
	"& .MuiSvgIcon-root": {
		color: `${theme.palette.common.white} !important`,
	},
	"&:hover": {
		bgcolor: theme.palette.primary.dark,
		borderColor: theme.palette.primary.dark,
		color: `${theme.palette.common.white} !important`,
		boxShadow: "none",
	},
});
